<template>
  <vz-dialog
    ref="dialog"
    title="На карте"
    big
    full-screen
    hide-footer
    name="map-select"
    class="vz-map-select-modal"
    @show="onShow"
  >
    <div class="vz-map-select-modal-body">
      <div class="vz-map-select-modal-body-map">
        <vz-yandex-map
          v-model="map"
          v-model:location="locationValue"
          class="vz-position-relative"
          @address-select="onResultSelect"
        >
          <template #additional>
            <vz-loader :status-loader="loader"/>
          </template>
          <yandex-map-listener :settings="{ onClick: onMapClick, onUpdate: onMapUpdate }"/>

          <vz-yandex-map-default-clusterer
            v-if="localDirectionType === 'address' && restrictedItems.length"
            :markers="restrictedItems"
            @marker-click="setRestrictedAddress($event.guid, false)"
          />

          <yandex-map-marker
            v-else-if="localDirectionType === 'address'"
            :settings="{
              coordinates: addressMarkerCoordinates,
              draggable: true,
              onDragEnd,
            }"
          >
            <vz-icon
              url="/svg/vz-geolocation-home-red.svg"
              size="40"
              class="marker-icon vz-cursor-grab"
            />
          </yandex-map-marker>

          <vz-yandex-map-default-clusterer
            v-else-if="localDirectionType === 'terminal'"
            :key="localTerminalGuid"
            :markers="terminalMarkers"
            @marker-click="setTerminal($event.guid, false)"
          />

          <vz-yandex-map-default-clusterer
            v-else-if="localDirectionType === 'pickupPoint'"
            :markers="pickupPointsMarkers"
            variant="blue"
            @marker-click="setPickupPoint($event.guid, false)"
          />
        </vz-yandex-map>
      </div>

      <div class="vz-map-select-modal-body-direction vz-position-relative">
        <vz-loader :status-loader="directionLoader"/>
        <vz-direction
          ref="direction"
          v-model:location-guid="localLocationGuid"
          v-model:direction="localDirectionType"
          :address="localAddress"
          :terminal-guid="localTerminalGuid"
          :pickup-point-guid="localPickupPointGuid"
          :direction-to="directionTo"
          :hide-switcher="hideSwitcher"
          :terminal-purpose="terminalPurpose"
          address-suggestion-off
          terminal-has-scheme-link
          class="vz-map-select-modal-direction"
          @update:address="onAddressChange"
          @update:terminal-guid="setTerminal($event, true)"
          @update:pickup-point-guid="setPickupPoint($event, true)"
          @update:location-guid="onLocationChange"
        />

        <contacts-terminals-schedule
          v-if="selectedTerminal"
          :terminal-guid="selectedTerminal?.guid"
          with-note
        />
        <contacts-terminals-schedule
          v-else-if="selectedPickupPoint"
          :terminal-guid="selectedPickupPoint?.guid"
          is-pickup-point
          with-note
        />
        <vz-button
          variant="primary"
          class="mx-auto my-15"
          big
          @click="submit"
        >
          Выбрать
        </vz-button>
      </div>
    </div>
    <!--    {{ addressMarkerCoordinates }}, {{ locationValue }}-->
    <!--    {{ `Долгота: ${addressMarker?.coordinates[0]?.toFixed?.(2)}<br>Широта: ${addressMarker?.coordinates[1]?.toFixed?.(2)}` }}-->
  </vz-dialog>
</template>

<script setup lang="ts">
import {PropType} from 'vue';
import {
  DaDataRecord,
  Location,
  OrderDirectionType,
  PickupPoint,
  RestrictedItem,
  Terminal,
  TerminalPurpose
} from '~/types/types';
import {useTerminalStore} from '~/stores/terminal';
import {getLocationByFiasGuid, getLocationDaDataGeo, searchAddressesByLocation} from '@/api/city';
import VzDialog from '~/components/ui-kit/vz-dialog.vue';
import VzDirection from '~/components/personal/order/direction/vz-direction.vue';
import {deepCopyObject, setYM} from '~/common/functions';
import {getPickupPoint, getPickupPointByBounds} from '~/api/order';
import VzYandexMap from '~/components/ui-kit/map/vz-yandex-map.vue';
import VzLoader from '~/components/ui-kit/vz-loader.vue';
import ContactsTerminalsSchedule from '~/components/public/contacts/contacts-terminals-schedule.vue';
import VzButton from '~/components/ui-kit/vz-button.vue';
import {asyncComputed, useDebounceFn, whenever} from '@vueuse/core';
import type {Feature, LngLat, LngLatBounds, YMap, YMapLocationRequest} from '@yandex/ymaps3-types';
import {YandexMapListener, YandexMapMarker} from 'vue-yandex-maps';
import VzYandexMapDefaultClusterer from '~/components/ui-kit/map/vz-yandex-map-default-clusterer.vue';
import type {MapEventUpdateHandler} from '@yandex/ymaps3-types/imperative/YMapListener';

const emit = defineEmits(['submit'])

const props = defineProps({
  location: {
    type: Object as PropType<Location>,
    default: () => ({})
  },
  address: {
    type: String,
    default: ''
  },
  terminalGuid: {
    type: String,
    default: ''
  },
  pickupPointGuid: {
    type: String,
    default: ''
  },
  directionType: {
    type: String as PropType<OrderDirectionType>,
    default: 'address'
  },
  hideSwitcher: {
    type: Boolean,
    default: false
  },
  terminalPurpose: {
    type: String as PropType<'' | TerminalPurpose>,
    default: ''
  },
})

const {$showToast} = useNuxtApp()
const {terminalSelectByLocation} = useTerminalStore()
const directionTo = inject('directionTo', false)
const defaultCoordinates: LngLat = [37.617815, 55.755773]
const defaultZoom = 10

const dialog = ref<typeof VzDialog>()
const direction = ref<typeof VzDirection>()

const loader = ref(false)
const directionLoader = ref(false)
const localRegion = ref('')
const localAddress = ref(props.address)
const localLocationGuid = ref(props.location?.guid)
const localTerminalGuid = ref(props.terminalGuid)
const localDirectionType = ref(props.directionType)
const localPickupPointGuid = ref(props.pickupPointGuid)
const addressMarkerCoordinates = ref<any>(props.location?.coordinates || defaultCoordinates)
const locationValue = ref<YMapLocationRequest>({
  center: defaultCoordinates, // starting position [lng, lat]
  zoom: defaultZoom, // starting zoom
})
const pickupPoints = shallowRef(new Set<string>())
const newBounds = shallowRef<LngLatBounds>()
const map = shallowRef<null | YMap>(null)

const selectedLocation = computed((): Location => {
  return direction.value?.selectedLocation || props.location
})

const restrictedItems = computed(() => {
  return (direction.value?.restrictedItems || []).map((item: RestrictedItem) => ({
    guid: item.address,
    coordinates: [item.longitude, item.latitude],
    markerIcon: item.address === localAddress.value
      ? ''
      : '/svg/pointer.svg',
  }))
})

const terminals = computed(() => {
  return terminalSelectByLocation(selectedLocation.value.guid, props.terminalPurpose)
})

const selectedTerminal = computed((): Terminal | false => {
  if (localDirectionType.value !== 'terminal') {
    return false
  }
  return terminals.value.find(t => t.guid === localTerminalGuid.value) || false
})

const selectedPickupPoint = asyncComputed(async () => {
  if (localDirectionType.value !== 'pickupPoint' || !localPickupPointGuid.value) {
    return false
  }
  return await getPickupPoint(localPickupPointGuid.value)
})

const terminalMarkers = computed(() => {
  return terminals.value.map(t => ({
    ...t,
    coordinates: [...t.coordinates].reverse(),
    markerIcon: t.guid === localTerminalGuid.value
      ? '/svg/vz-geolocation-terminal-red.svg'
      : '/svg/vz-geolocation-terminal-grey.svg',
  }))
})

const pickupPointsMarkers = computed(() => {
  const result: PickupPoint[] = []
  pickupPoints.value.forEach(p => {
    const pup = JSON.parse(p) as any
    result.push({
      ...pup,
      coordinates: [pup.coords.lon, pup.coords.lat],
      markerIcon: pup.guid === localPickupPointGuid.value
        ? '/svg/vz-geolocation-yandex-red.svg'
        : '/svg/vz-geolocation-yandex-grey.svg',
    })
  })

  return result
})

async function setupAddressMarker() {
  const value = `${selectedLocation.value.region_s} ${selectedLocation.value.name} ${localAddress.value}`
  const zoom = localAddress.value ? 15 : +(selectedLocation.value?.zoom || 10)

  const searchResponse = await ymaps3.search({
    text: value,
    limit: 1
  })
  const coordinates = searchResponse[0]?.geometry?.coordinates
  if (coordinates) {
    setCenter({coords: coordinates, zoom})
    addressMarkerCoordinates.value = coordinates
  }
}

async function initSettings() {
  await nextTick()
  switch (localDirectionType.value) {
    case 'address':
      return setupAddressMarker()
    case 'pickupPoint':
      loader.value = true
      return onBoundsChange(map.value?.bounds).finally(() => loader.value = false)
  }
}

async function setLocationByCoords(coords: LngLat) {
  let searchResult = null as never as Feature
  let daDataResult = null as never as DaDataRecord
  loader.value = true

  await Promise.all([
    ymaps3.search({text: coords.toString(), limit: 1}).then(res => {
      searchResult = res?.[0]
    }).catch(e => console.error(e)),
    getLocationDaDataGeo({lon: coords[0], lat: coords[1]}).then(res => {
      daDataResult = res?.[0]
    }).catch(e => console.error(e)),
  ])

  if (!daDataResult) {
    loader.value = false
    return
  }

  coords = searchResult?.geometry?.coordinates || [+daDataResult.data.geo.lon, +daDataResult.data.geo.lat]
  let address = searchResult?.properties.description || ''

  if (daDataResult.region.includes(address)) {
    // Если yandex нашёл только обобщённый адрес (например село Кукуево) то игнорируем такой результат
    address = ''
  }

  // Проверка на смену города
  let checkedAddress = (await searchAddressesByLocation(
      daDataResult?.daDataShame || `${daDataResult?.region}, ${address || daDataResult?.value}`, selectedLocation.value, {check: true})
  )?.[0]?.data

  // TASK-295420
  if (!checkedAddress) {
    $showToast({
      variant: 'warning',
      title: 'Внимание!',
      text: `Укажите "Населённый пункт" вручную`,
      autocloseDelay: 10000,
      iconName: 'information-outline'
    })
    loader.value = false
    return
  }

  let newFias = checkedAddress?.district_fias || checkedAddress?.fias || daDataResult.data.fias

  if (newFias !== selectedLocation.value.fias) {
    // Смена города
    let newLocations = await getLocationByFiasGuid(newFias)
    if (!checkedAddress) {
      // Двойная смена города (когда на карте выбрали вообще другой регион)
      checkedAddress = (await searchAddressesByLocation(
          `${daDataResult?.region}, ${address || daDataResult?.value}`, newLocations[0], {check: true})
      )?.[0]?.data
      newFias = checkedAddress?.district_fias || checkedAddress?.fias || daDataResult.data.fias
      newLocations = await getLocationByFiasGuid(newFias)
    }
    if (newLocations?.[0]) {
      localLocationGuid.value = newLocations[0].guid
      setCenter({coords/*, zoom: newLocations[0].zoom*/})
    }
  }

  // смена адреса
  if (localDirectionType.value === 'address') {
    localAddress.value = address || daDataResult?.value
    localRegion.value = daDataResult?.region
  }
  loader.value = false
}

async function setTerminal(guid: string, center = false) {
  if (localDirectionType.value !== 'terminal') {
    return
  }
  const target = terminals.value.find(t => t.guid === guid)
  localTerminalGuid.value = guid

  if (center && target?.coordinates) {
    setCenter({lon: target.coordinates[1], lat: target.coordinates[0], zoom: 10})
  }
}

async function setRestrictedAddress(guid: string, center = false) {
  if (localDirectionType.value === 'address' && restrictedItems.value.length) {
    localAddress.value = guid
  }
}

async function setPickupPoint(guid: string, center = false) {
  if (localDirectionType.value !== 'pickupPoint' || !guid) {
    return
  }

  localPickupPointGuid.value = guid
  directionLoader.value = true
  const target = await getPickupPoint(guid).finally(() => {
    directionLoader.value = false
  })
  localLocationGuid.value = target.locationGuid

  if (center) {
    setCenter({...target.coords, zoom: 15})
  }
}

function onAddressChange(address: string, obj: any): void {
  localAddress.value = address
  if (obj?.geo) {
    addressMarkerCoordinates.value = [obj.geo.lon, obj.geo.lat]
    setCenter(obj.geo)
  }
}

function setCenter(options: { lat?: number | string, lon?: number | string, coords?: LngLat, zoom?: number | string | null }) {
  let {lat, lon, coords, zoom} = deepCopyObject(options)
  if (!coords) {
    coords = [+(lon || 0), +(lat || 0)]
  }
  if (zoom) {
    locationValue.value = {
      center: coords,
      zoom: +zoom
    }
  } else {
    locationValue.value = {center: coords}
  }
}

async function onLocationChange() {
  // Дожидаемся смены города
  await nextTick()
  await nextTick()
  if (localPickupPointGuid.value) {
    const pickupPoint = await getPickupPoint(localPickupPointGuid.value)
    if (pickupPoint.locationGuid !== localLocationGuid.value) {
      localPickupPointGuid.value = ''
    }
  }
  if (localDirectionType.value === 'address') {
    await setupAddressMarker()
  } else if (localDirectionType.value === 'terminal') {
    await setTerminal(localTerminalGuid.value, true)
  } else if (localDirectionType.value === 'pickupPoint') {
    setCenter({coords: selectedLocation.value.coordinates as any, zoom: selectedLocation.value.zoom})
  }
}

const onMapUpdate: MapEventUpdateHandler = (e) => {
  if (e.location.bounds) {
    newBounds.value = e.location.bounds
  }
}

const debouncedBoundsChange = useDebounceFn((bounds: LngLatBounds) => {
  onBoundsChange(bounds)
}, 1000)

async function onBoundsChange(bounds?: LngLatBounds) {
  const startLength = pickupPoints.value.size
  await nextTick()
  if (localDirectionType.value === 'pickupPoint' && bounds) {
    getPickupPointByBounds(bounds, 10000).then(list => {
      (list.data || []).forEach(pup => {
        pickupPoints.value.add(JSON.stringify(pup))
      })
      if (startLength !== pickupPoints.value.size) {
        triggerRef(pickupPoints)
      }
    }).finally(() => loader.value = false)
  }
}

function onMapClick(_: any, {coordinates}: { coordinates: LngLat }) {
  if (localDirectionType.value !== 'address' || (localDirectionType.value === 'address' && restrictedItems.value.length)) {
    return
  }

  setLocationByCoords(coordinates)
  addressMarkerCoordinates.value = coordinates
}

function onDragEnd(coords: LngLat) {
  if (localDirectionType.value !== 'address' || (localDirectionType.value === 'address' && restrictedItems.value.length)) {
    return
  }
  if (coords) {
    setLocationByCoords(coords)
    addressMarkerCoordinates.value = coords
  }
}

// yandex поиск
async function onResultSelect(coords: LngLat) {
  addressMarkerCoordinates.value = coords
  await setLocationByCoords(coords)
}

function submit() {
  dialog.value?.hide()
  setYM(38058485, ('map' + localDirectionType.value[0].toUpperCase() + localDirectionType.value.slice(1)), 'vozovoz.ru')

  emit('submit', {
    location: localLocationGuid.value,
    terminal: localTerminalGuid.value,
    pickupPoint: localPickupPointGuid.value,
    address: localAddress.value,
    region: localRegion.value,
    direction: localDirectionType.value,
    selectedLocation: selectedLocation.value
  })
}

function onShow() {
  setCenter({
    coords: selectedLocation.value?.coordinates as any,
    zoom: selectedLocation.value?.zoom || defaultZoom
  })
  localLocationGuid.value = props.location?.guid
  localAddress.value = props.address
  localTerminalGuid.value = props.terminalGuid
  localPickupPointGuid.value = props.pickupPointGuid
  localDirectionType.value = props.directionType

  if (map.value) {
    initSettings()
  }
}

function show() {
  dialog.value?.show()
}

watch(localDirectionType, initSettings)

watch(newBounds, (newValue, oldValue) => {
  if (newValue && newValue?.toString() !== oldValue?.toString()) {
    debouncedBoundsChange(newValue)
  }
})

whenever(map, initSettings)

defineExpose({show})
</script>

<style lang="scss" scoped>
.vz-map-select-modal {
  &-body {
    display: flex;
    gap: 20px;
    min-height: 670px;

    &-direction {
      width: 350px;
      flex-shrink: 0;
    }

    &-map {
      width: 100%;
      height: 100%;
      min-height: 420px;
    }

    @media screen and (min-width: $md) and (max-width: $lg) {
      gap: 10px;
    }

    @media screen and (max-width: $md) {
      flex-direction: column;
      margin-right: 0;

      &-direction {
        width: unset;
        margin: 0 8px;
      }
    }
  }
}
</style>

<style lang="scss">
.vz-map-select-modal.full-screen {
  .vz-yandex-map {
    flex: 1;
    height: 670px;
    background-color: $box-gray;

    .marker-icon-default {
      width: 28px;
      height: 40px;
    }

    @media screen and (max-width: $lg) {
      height: 640px;
    }
    @media screen and (max-width: $md) {
      height: 400px;
    }
  }

  .vz-dialog-card-header {
  }

  .vz-dialog-card-content {
    height: 100%;

    &-wrapper {
      height: 100%;
      padding: 0;
    }
  }

  .contacts-terminals-card-schedule {
    @media screen and (max-width: $sm) {
      justify-content: space-between;
    }
  }

  .vz-dialog-card-header,
  .vz-dialog-card-content {
    @media screen and (min-width: $sm) {
      width: 1240px;
      max-width: calc(100% - 16px);
    }
  }

  .vz-dialog-card-header-title {
    margin-left: 0;
    @media screen and (max-width: $sm) {
      margin-left: 8px;
    }
  }
}

.vz-map-select-modal-direction {
  display: flex;
  gap: 10px;
  flex-direction: column;

  .vz-switcher {
    order: 1;
    margin-top: 0 !important;
  }

  .vz-new-autocomplete, .vz-select-dropdown {
    order: 2;
  }

  .vz-new-autocomplete:last-child {
    min-height: 70px;
    display: flex;
    align-items: flex-end;
  }

  .vz-input-control.big {
    height: 48px;
    padding: 12.5px 14px;
    font-size: 16px;
  }

  .vz-input-label {
    color: $font-low;
    font-size: $fs-14;
    height: 17px;
    margin-bottom: 7px;
    font-weight: 500;
  }
}
</style>
